/* eslint-disable @typescript-eslint/no-shadow */
import { useEffect } from 'react';
import jwtDecode from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { paths, randomId } from '@app/constants';
import {
  getCurrentAppTheme,
  isCountryCodeOnly,
  useHandleFormErrors,
  useHydration,
} from '@app/utils';
import {
  useAcceptInvite,
  useCreateNewTeam,
  useRefreshSession,
  useTokenData,
  useTokenUserData,
  useTrialManagerSignUp,
  useUpdateUserDetails,
} from '@data/hooks';
import { APIError, IToken } from '@data/index';
import { Language, Role, TokenType, TrialManagerSignUpBody } from '@data/sdk/match';
import { useAppUserStore, useB2BSignUpFormStore } from '@data/storage';
import { useTrackEvents } from '@data/utils/events';
import { AntdForm } from '@ui/components';
import { useSignUpTracking } from '../../../hooks';

type CreateUserFormValues = {
  confirmPassword?: string | null;
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
  password?: string | null;
  phoneNumber?: string | null;
};

type AcceptInviteTokenType = typeof TokenType.Invite | typeof TokenType.TeamInvite;

export const useCreateUser = () => {
  const navigate = useNavigate();
  const [token] = useQueryParam('token', StringParam);
  const [form] = AntdForm.useForm<CreateUserFormValues>();

  const { user } = useAppUserStore();
  const hasHydrated = useHydration();
  const { theme } = getCurrentAppTheme();
  const { setFormStep } = useB2BSignUpFormStore();
  const { events, trackEvent } = useTrackEvents();
  const { trackSignUp, trackSignUpFormError } = useSignUpTracking();
  const { email, firstName, lastName, phoneNumber } = useTokenUserData();

  const { data: tokenData, isFetching } = useTokenData(token ?? '');

  const { mutate: signUp } = useTrialManagerSignUp();
  const { mutate: createTeam } = useCreateNewTeam();
  const { mutate: acceptInvite } = useAcceptInvite();
  const { mutate: refreshSession } = useRefreshSession();
  const { mutate: updateUserDetails } = useUpdateUserDetails();

  const trackSignUpEvent = (email: string) => {
    const { user: userState } = useAppUserStore.getState();

    if (userState?.currentRole === Role.TrialManager) {
      trackSignUp(email);
    } else {
      trackEvent(events.SignUp, {
        userId: randomId,
        role: userState?.currentRole ?? Role.TrialManager,
        theme,
      });
    }
  };

  const onError = (error: APIError<TrialManagerSignUpBody>) => {
    useHandleFormErrors(error, form);
    trackSignUpFormError(
      error?.response?.data?.message ?? '',
      error?.response?.status?.toString() ?? '',
    );
  };

  const handleUpdateUserData = (values: TrialManagerSignUpBody) => {
    updateUserDetails(values, {
      onError: (err) => useHandleFormErrors(err, form),
      onSuccess: () => {
        if (user) {
          refreshSession({ refreshToken: user.refreshToken }, { onSuccess: () => setFormStep(1) });
        }
      },
    });
  };

  const handleCreateTeam = ({
    name,
    token,
    tokenUserId,
    refreshToken,
  }: {
    name: string;
    refreshToken: string;
    token: string;
    tokenUserId: string;
  }) => {
    createTeam(
      {
        userId: tokenUserId,
        name,
        role: Role.PatientOrg,
        teamRole: 'Expert',
        token,
      },
      {
        onSuccess: (team) => {
          refreshSession(
            { teamId: team.id, refreshToken },
            {
              onSuccess: () => {
                navigate(`/${paths.app}/${paths.trial_recruitment}`);
                window.location.reload(); // TODO: improve
              },
            },
          );
        },
      },
    );
  };

  const handleCreateUser = (values: CreateUserFormValues) => {
    const { phoneNumber } = values;
    const transformedValues: TrialManagerSignUpBody = {
      theme,
      email: values.email ?? '',
      firstName: values.firstName ?? '',
      lastName: values.lastName ?? '',
      password: values.password ?? '',
      language: Language.En,
      role: tokenData?.role ?? Role.TrialManager,
      ...(!!phoneNumber && !isCountryCodeOnly(phoneNumber) ? { phoneNumber } : {}),
    };

    if (token) {
      if (
        [TokenType.TeamInvite, TokenType.Invite].includes(
          tokenData?.tokenType as AcceptInviteTokenType,
        )
      ) {
        acceptInvite(
          { ...transformedValues, theme, tokenValue: token, teamRole: tokenData?.teamRole },
          {
            onSuccess: () => {
              trackSignUpEvent(transformedValues.email);
              navigate(`/${paths.app}`, { replace: true });
              window.location.reload(); // TODO: improve
            },
            onError,
          },
        );
      } else {
        signUp(
          { ...transformedValues, theme },
          {
            onSuccess: (res) => {
              trackSignUpEvent(transformedValues.email);

              const { user_id: tokenUserId } = jwtDecode<IToken>(res.token);
              if (tokenData?.role === Role.PatientOrg) {
                handleCreateTeam({
                  name: tokenData?.teamName ?? '',
                  token,
                  tokenUserId,
                  refreshToken: res.refreshToken,
                });
              } else {
                setFormStep(1);
              }
            },
            onError,
          },
        );
      }
    } else if (user) {
      handleUpdateUserData(transformedValues);
    } else {
      signUp(transformedValues, {
        onError,
        onSuccess: () => {
          setFormStep(1);
          trackSignUpEvent(transformedValues.email);
        },
      });
    }
  };

  const initialValues: CreateUserFormValues = {
    email: token ? tokenData?.email : email,
    password: null,
    confirmPassword: null,
    firstName: token ? tokenData?.firstName : firstName,
    lastName: token ? tokenData?.lastName : lastName,
    phoneNumber: phoneNumber ?? '',
  };

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [tokenData]);

  useEffect(() => {
    // additionally check for team since user might be already logged in but not have a team yet
    // and be on the create team step
    if (hasHydrated && user && user.teamId) {
      navigate(`/${paths.app}`);
    }
  }, [user, hasHydrated]);

  return {
    form,
    tokenData,
    initialValues,
    dataIsLoading: isFetching,
    handleCreateUser,
    userRole: tokenData?.role,
    editUserMode: !!user && !token,
  };
};
